// src/pages/admin/Settings.jsx
import React, { useState, useEffect } from 'react';
import { useApp } from 'contexts/AppContext';
import { useAuth } from 'contexts/AuthContext';
import {
  businessConfigService, establishmentsService, authService, DEFAULT_BUSINESS_CONFIG,
} from 'infrastructure/firebase';
import { Button, PhotoUpload } from 'shared/components/ui';
import { useMobile } from 'shared/hooks/useMobile';
import { ESTABLISHMENT_CATEGORIES } from 'shared/config/plans';

const DAYS_LABELS = ['Domingo','Segunda-feira','Terça-feira','Quarta-feira','Quinta-feira','Sexta-feira','Sábado'];
const DAYS_SHORT  = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

const T = {
  brand:  '#6B5876', accent: '#C9929A', border: '#E8E5EC',
  s2:     '#F6F4F9', s3: '#F0EDF3',
  text:   '#3D3447', text2: '#6B6070', muted: '#A8A1B0',
  sans:   "'DM Sans', sans-serif",
  serif:  "'Instrument Serif', serif",
};

const inputStyle = {
  width: '100%', padding: '11px 14px', border: `1.5px solid ${T.border}`,
  borderRadius: 10, fontSize: 14, fontFamily: T.sans, color: T.text,
  outline: 'none', boxSizing: 'border-box', background: '#fff',
};

// ── Upload de banner (formato retangular) ──────────────────────────────────────
function BannerUpload({ value, onChange }) {
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const canvas = document.createElement('canvas');
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 1200, MAX_H = 400;
        let { width: w, height: h } = img;
        const ratio = Math.min(MAX_W / w, MAX_H / h, 1);
        canvas.width  = Math.round(w * ratio);
        canvas.height = Math.round(h * ratio);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        onChange(canvas.toDataURL('image/jpeg', 0.80));
      };
      img.src = ev.target?.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <label style={{ cursor: 'pointer', display: 'block' }}>
      <div style={{
        width: '100%', height: 160, borderRadius: 12, overflow: 'hidden',
        border: `2px dashed ${T.border}`, background: T.s2,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundImage: value ? `url(${value})` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center',
        position: 'relative', transition: 'border-color 0.15s',
      }}>
        {!value && (
          <div style={{ textAlign: 'center', color: T.muted, fontFamily: T.sans }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🖼️</div>
            <div style={{ fontSize: 13 }}>Clique para adicionar banner / capa</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>Recomendado: 1200×400px</div>
          </div>
        )}
        {value && (
          <div style={{ position: 'absolute', bottom: 8, right: 8,
            background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 11,
            padding: '4px 10px', borderRadius: 20, fontFamily: T.sans }}>
            Clique para alterar
          </div>
        )}
      </div>
      <input type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
    </label>
  );
}

export default function AdminSettings() {
  const isMobile  = useMobile();
  const { showToast, refreshBusinessConfig, establishment } = useApp();
  const { estabelecimentoId } = useAuth();

  const [tab, setTab] = useState('horario');

  // ── Troca de senha ───────────────────────────────────────────────────────────
  const [pwCurrent, setPwCurrent]   = useState('');
  const [pwNext, setPwNext]         = useState('');
  const [pwConfirm, setPwConfirm]   = useState('');
  const [pwError, setPwError]       = useState('');
  const [savingPw, setSavingPw]     = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    if (!pwCurrent)              { setPwError('Informe a senha atual.'); return; }
    if (pwNext.length < 6)       { setPwError('Nova senha deve ter no mínimo 6 caracteres.'); return; }
    if (pwNext === pwCurrent)    { setPwError('A nova senha deve ser diferente da atual.'); return; }
    if (pwNext !== pwConfirm)    { setPwError('As senhas não coincidem.'); return; }
    setSavingPw(true);
    try {
      await authService.changePassword(pwCurrent, pwNext);
      showToast('Senha alterada com sucesso!');
      setPwCurrent(''); setPwNext(''); setPwConfirm('');
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setPwError('Senha atual incorreta. Tente novamente.');
      } else if (code === 'auth/too-many-requests') {
        setPwError('Muitas tentativas. Aguarde alguns minutos.');
      } else {
        setPwError('Erro ao alterar senha. Tente novamente.');
      }
    } finally { setSavingPw(false); }
  };

  // ── Horário de funcionamento ─────────────────────────────────────────────────
  const [form, setForm]     = useState({ ...DEFAULT_BUSINESS_CONFIG });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    businessConfigService.get()
      .then(cfg => setForm({
        workDays:  cfg.workDays  ?? DEFAULT_BUSINESS_CONFIG.workDays,
        workStart: cfg.workStart ?? DEFAULT_BUSINESS_CONFIG.workStart,
        workEnd:   cfg.workEnd   ?? DEFAULT_BUSINESS_CONFIG.workEnd,
        name:      cfg.name      ?? DEFAULT_BUSINESS_CONFIG.name,
      }))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const toggleDay = (dow) => setForm(f => {
    const has  = f.workDays.includes(dow);
    const next = has ? f.workDays.filter(d => d !== dow) : [...f.workDays, dow].sort();
    return { ...f, workDays: next };
  });

  const handleSaveHorario = async () => {
    setSaveError('');
    if (form.workDays.length === 0) { showToast('Selecione ao menos um dia.', 'warning'); return; }
    if (form.workStart >= form.workEnd) { showToast('Horário de abertura deve ser antes do fechamento.', 'warning'); return; }
    setSaving(true);
    try {
      await businessConfigService.set(form);
      await refreshBusinessConfig();
      showToast('Configurações salvas!');
    } catch (err) {
      const msg = err?.code === 'permission-denied'
        ? 'Permissão negada. Verifique as regras do Firestore.'
        : err?.message || 'Erro ao salvar.';
      setSaveError(msg);
      showToast('Erro ao salvar configurações.', 'danger');
    } finally { setSaving(false); }
  };

  // ── Informações da clínica ───────────────────────────────────────────────────
  const [clinicForm, setClinicForm] = useState({
    foto: '', banner: '', descricao: '', nomeFantasia: '', categoria: '',
  });
  const [savingClinic, setSavingClinic] = useState(false);

  useEffect(() => {
    if (establishment) {
      setClinicForm({
        foto:        establishment.foto        || '',
        banner:      establishment.banner      || '',
        descricao:   establishment.descricao   || '',
        nomeFantasia:establishment.nomeFantasia|| '',
        categoria:   establishment.categoria   || '',
      });
    }
  }, [establishment]);

  const handleSaveClinic = async () => {
    if (!estabelecimentoId) {
      showToast('Nenhuma clínica vinculada a este usuário.', 'warning');
      return;
    }
    setSavingClinic(true);
    try {
      await establishmentsService.update(estabelecimentoId, {
        foto:        clinicForm.foto        || null,
        banner:      clinicForm.banner      || null,
        descricao:   clinicForm.descricao.slice(0, 300),
        nomeFantasia:clinicForm.nomeFantasia,
        categoria:   clinicForm.categoria,
      });
      showToast('Informações da clínica salvas!');
    } catch {
      showToast('Erro ao salvar. Tente novamente.', 'danger');
    } finally { setSavingClinic(false); }
  };

  if (!loaded) return <div style={{ fontFamily: T.sans, color: T.muted, padding: 40 }}>Carregando...</div>;

  return (
    <div style={{ maxWidth: 680, fontFamily: T.sans }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: T.serif, fontSize: 28, color: T.text }}>Configurações</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>
          Gerencie os dados do estabelecimento e horários de funcionamento.
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', background: '#F3F0F7', borderRadius: 12, padding: 4, marginBottom: 24, gap: 4 }}>
        {[
          { id: 'horario',   label: '⏰ Funcionamento' },
          { id: 'clinica',   label: '🏥 Clínica' },
          { id: 'seguranca', label: '🔒 Segurança' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ flex: 1, padding: '9px 0', border: 'none', cursor: 'pointer',
              borderRadius: 9, fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
              fontFamily: T.sans,
              background: tab === t.id ? '#fff' : 'transparent',
              color: tab === t.id ? T.text : T.muted,
              boxShadow: tab === t.id ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
              transition: 'all 0.2s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Aba Funcionamento ──────────────────────────────────────────────────── */}
      {tab === 'horario' && (
        <>
          {/* Nome */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
              Nome do estabelecimento
            </div>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ex: Studio Bella Arte" style={inputStyle} />
          </div>

          {/* Dias */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
              Dias de funcionamento
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : 'repeat(7, 1fr)', gap: 8 }}>
              {[0,1,2,3,4,5,6].map(dow => {
                const active = form.workDays.includes(dow);
                return (
                  <button key={dow} onClick={() => toggleDay(dow)} style={{
                    padding: '12px 4px', borderRadius: 12,
                    border: `2px solid ${active ? T.brand : T.border}`,
                    background: active ? T.brand : T.s2, color: active ? '#fff' : T.muted,
                    fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all .15s',
                  }}>
                    {DAYS_SHORT[dow]}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: T.muted }}>
              {form.workDays.length === 0
                ? '⚠️ Nenhum dia selecionado.'
                : `Funcionamento: ${form.workDays.map(d => DAYS_LABELS[d]).join(', ')}`}
            </div>
          </div>

          {/* Horário */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: 24, marginBottom: 28 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>
              Horário de funcionamento
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: T.muted, display: 'block', marginBottom: 6 }}>Abertura</label>
                <input type="time" value={form.workStart}
                  onChange={e => setForm(f => ({ ...f, workStart: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ fontSize: 20, color: T.muted, marginTop: 20 }}>→</div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: T.muted, display: 'block', marginBottom: 6 }}>Fechamento</label>
                <input type="time" value={form.workEnd}
                  onChange={e => setForm(f => ({ ...f, workEnd: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: T.muted }}>
              {Math.max(0, Math.floor((
                parseInt(form.workEnd.split(':')[0]) * 60 + parseInt(form.workEnd.split(':')[1]) -
                parseInt(form.workStart.split(':')[0]) * 60 - parseInt(form.workStart.split(':')[1])
              ) / 30))} horários de 30 min por profissional
            </div>
          </div>

          {saveError && (
            <div style={{ background: '#fff0f0', border: '1.5px solid #fca5a5', borderRadius: 12,
              padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#b91c1c', lineHeight: 1.5 }}>
              ❌ {saveError}
            </div>
          )}
          <Button variant="primary" loading={saving} onClick={handleSaveHorario}
            style={{ width: '100%', padding: '14px 0', fontSize: 15 }}>
            💾 Salvar Configurações
          </Button>
        </>
      )}

      {/* ── Aba Informações da Clínica ─────────────────────────────────────────── */}
      {tab === 'clinica' && (
        <>
          {!estabelecimentoId && (
            <div style={{ background: '#FFF9E6', border: '1px solid #FCD34D', borderRadius: 12,
              padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#92400E' }}>
              ℹ️ Esta seção é para administradores vinculados a uma clínica cadastrada.
              Super admins não possuem estabelecimento vinculado.
            </div>
          )}

          {/* Banner */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase',
              letterSpacing: 1, marginBottom: 14 }}>
              Imagem de capa (banner)
            </div>
            <BannerUpload
              value={clinicForm.banner}
              onChange={v => setClinicForm(f => ({ ...f, banner: v }))}
            />
          </div>

          {/* Foto de perfil + Dados básicos */}
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase',
              letterSpacing: 1, marginBottom: 16 }}>
              Perfil da clínica
            </div>

            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 16,
              flexDirection: isMobile ? 'column' : 'row' }}>
              <div>
                <div style={{ fontSize: 12, color: T.text2, marginBottom: 8 }}>Foto de perfil</div>
                <PhotoUpload
                  value={clinicForm.foto}
                  onChange={v => setClinicForm(f => ({ ...f, foto: v }))}
                  emoji="🏥"
                  shape="rect"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                    Nome Fantasia
                  </label>
                  <input value={clinicForm.nomeFantasia}
                    onChange={e => setClinicForm(f => ({ ...f, nomeFantasia: e.target.value }))}
                    placeholder="Nome exibido para clientes" style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 8 }}>
                    Categoria
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {ESTABLISHMENT_CATEGORIES.map(cat => (
                      <button key={cat.id} type="button"
                        onClick={() => setClinicForm(f => ({ ...f, categoria: cat.id }))}
                        style={{ padding: '8px 10px', border: `1.5px solid ${clinicForm.categoria === cat.id ? T.brand : T.border}`,
                          borderRadius: 8, cursor: 'pointer',
                          background: clinicForm.categoria === cat.id ? T.s3 : '#fff',
                          display: 'flex', alignItems: 'center', gap: 6, fontFamily: T.sans,
                          color: clinicForm.categoria === cat.id ? T.brand : T.text2, fontSize: 12 }}>
                        <span>{cat.icon}</span>
                        <span style={{ fontWeight: clinicForm.categoria === cat.id ? 600 : 400 }}>{cat.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Descrição */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                Descrição{' '}
                <span style={{ fontSize: 11, color: clinicForm.descricao.length > 280 ? T.accent : T.muted, fontWeight: 400 }}>
                  ({clinicForm.descricao.length}/300 caracteres)
                </span>
              </label>
              <textarea
                value={clinicForm.descricao}
                onChange={e => setClinicForm(f => ({ ...f, descricao: e.target.value.slice(0, 300) }))}
                rows={4}
                placeholder="Descreva sua clínica, serviços oferecidos, diferenciais..."
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
              />
            </div>
          </div>

          <Button variant="primary" loading={savingClinic} onClick={handleSaveClinic}
            disabled={!estabelecimentoId}
            style={{ width: '100%', padding: '14px 0', fontSize: 15 }}>
            💾 Salvar Informações da Clínica
          </Button>
        </>
      )}

      {/* ── Aba Segurança ──────────────────────────────────────────────────────── */}
      {tab === 'seguranca' && (
        <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.muted, textTransform: 'uppercase',
            letterSpacing: 1, marginBottom: 4 }}>
            Alterar senha
          </div>
          <p style={{ fontSize: 13, color: T.text2, marginBottom: 20, lineHeight: 1.5 }}>
            Para alterar sua senha, confirme a senha atual e defina uma nova.
          </p>

          {pwError && (
            <div style={{ padding: '10px 14px', background: '#F0DADD', borderRadius: 8,
              color: '#7A3C44', fontSize: 13, marginBottom: 16 }}>{pwError}</div>
          )}

          <form onSubmit={handleChangePassword}>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                Senha atual
              </label>
              <input type="password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)}
                required placeholder="••••••••" autoComplete="current-password" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                Nova senha <span style={{ fontSize: 11, color: T.muted, fontWeight: 400 }}>(mín. 6 caracteres)</span>
              </label>
              <input type="password" value={pwNext} onChange={e => setPwNext(e.target.value)}
                required placeholder="••••••••" autoComplete="new-password" style={inputStyle} />
            </div>
            <div style={{ marginBottom: 22 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: T.text2, marginBottom: 5 }}>
                Confirmar nova senha
              </label>
              <input type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)}
                required placeholder="••••••••" autoComplete="new-password"
                style={{ ...inputStyle,
                  borderColor: pwConfirm && pwNext !== pwConfirm ? T.accent : T.border }} />
              {pwConfirm && pwNext !== pwConfirm && (
                <span style={{ fontSize: 11, color: T.accent, marginTop: 3, display: 'block' }}>
                  As senhas não coincidem
                </span>
              )}
            </div>
            <Button type="submit" variant="primary" loading={savingPw} disabled={savingPw}
              style={{ width: '100%', padding: '14px 0', fontSize: 15 }}>
              🔒 Alterar senha
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}
